<template>
  <div class="">
    <tree
      :model="treeData"
      class="tree"
      @node:selected="nodeSelected"
      @tree:updated="treeUpdated"
      @tree:inflated="treeInflated"
      v-show="!loading">
      <template slot-scope="{ node }">
        <div>
          <label v-if="node.isProximateOrDistalGoal() === true"><strong>{{ node.getGoalText() }}</strong></label>
          <span v-if="!node.state.isBeingEdited && node.isProximateOrDistalGoal() === true"
                class="proxdistalgoal-label"
                :class="node.labelStyle">
            {{ node.label }}
          </span>
          <span v-else-if="!node.state.isBeingEdited"
                class="label"
                :class="node.labelStyle">
            {{ node.label }}
          </span>
          <b-form v-else
                  @submit.prevent="stopEditing(node, $event)">
            <input class="node-input"
                   :class="node.labelStyle"
                   size="20"
                   :ref="`input_${node.id}`"
                   @blur="stopEditing(node, $event)"
                   type="text"
                   v-model="node.label" />
          </b-form>
          <span class="sub-label">
            <template v-if="typeof node.subLabel === 'string'">
              {{ node.subLabel }}
            </template>
            <template v-else-if="node.parent && node.data.evaluated">
              <span class="pl-3">
                <span v-if="node.subLabel.default">
                  {{ node.subLabel.default | scoreDisplay}}
                </span>
                <span>(</span>
                <span v-if="node.subLabel.success"
                      class="text-success">
                  +{{ node.subLabel.success | pnDisplay}}
                </span>,
                <span v-if="node.subLabel.danger"
                      class="text-danger">
                  -{{ node.subLabel.danger | pnDisplay}}
                </span>
                <span>)</span>
              </span>
            </template>
          </span>
        </div>
        <div class="d-flex justify-content-between align-items-center my-0 py-0 w-100">
          <span class="actions">
            Actions
          </span>
          <span>
            <button v-if="node.showEditButton()"
                    :disabled="node.disabled"
                    type="button"
                    v-b-tooltip.hover
                    :title="`Rename ${node.getNodeTitle().single}`"
                    @click.stop="startEditing(node)"
                    class="btn btn-link pr-0 py-0">
              <font-awesome-icon icon="edit"
                                 size="xs" />
            </button>
            <button v-if="node.showRemoveButton()"
                    :disabled="node.disabled"
                    type="button"
                    v-b-tooltip.hover
                    :title="`Remove ${node.getNodeTitle()[node.hasChildren() ? 'plural' : 'single']}`"
                    @click.stop="removeNode(node)"
                    class="btn btn-link ml-2 ml-md-0 pr-0 py-0">
              <font-awesome-icon icon="trash"
                                 size="xs" />
            </button>
            <button v-if="node.showAddButton()"
                    :disabled="node.disabled"
                    type="button"
                    v-b-tooltip.hover
                    :title="`Add ${node.getNodeTitle(true).plural}`"
                    @click.stop="addNode(node)"
                    class="btn btn-link ml-2 ml-md-0 pr-0 py-0">
              <font-awesome-icon v-if="node.parent"
                                 icon="sitemap"
                                 :rotation="270"
                                 size="xs" />
              <font-awesome-icon v-else
                                 icon="plus"
                                 size="xs" />
            </button>
          </span>
        </div>
      </template>
    </tree>
    <div
      class="loading"
      v-show="loading">
      <font-awesome-icon
        icon="spinner"
        size="2x"
        spin/>
    </div>
    <!-- The modal -->
    <b-modal
      v-model="modal.show"
      centered
      no-fade
      ok-only>
      <template slot="modal-title">
        <div
          class="text-info"
          v-html="modal.title"/>
      </template>
      <div v-html="modal.text" />
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  initNodeData, evalTree, getNodeLabel, filters
} from '@/utils'
import tree from '@/components/tree.vue'

export default {
  name: 'TreePanel',
  components: {
    tree
  },
  props: {
    treeUpdated: {
      type: Function,
      default: () => {}
    }
  },
  filters,
  methods: {
    treeInflated ({ inflated, deflated }) {
      // Evaluate inflated tree
      this.processInflatedTree(inflated)
      // restore 'selectedNode' from the saved state or set it to root if no saves found
      const selectedNode = inflated.nodes.find(n => n.state.selected)
      this.$store.dispatch(
        'SetNode',
        selectedNode || inflated.root
      )
      if (deflated.fresh) {
        // extra push for reset/loaded tree
        window.history.pushState({ treeData: deflated }, '')
      }
    },
    processInflatedTree (inflatedTree) {
      evalTree(inflatedTree.root)
      // const result = evalTree(inflatedTree.root)
      // if (!this.modal.show && result && result.hash !== this.modal.hash) {
      //   this.modal = {
      //     text: result.message,
      //     title: result.title,
      //     show: true,
      //     hash: result.hash,
      //   }
      // }
    },
    addNode(node) {
      this.$root.$emit('bv::hide::tooltip')

      //console.log('+++++++node.usageType ' + node.usageType)

      node.append({
        data: this.initNodeData()
      }, false)
    },
    removeNode (node) {
      this.$root.$emit('bv::hide::tooltip')
      node.remove()
      this.$store.dispatch('SetNode', null)
    },
    nodeSelected (node) {
      this.$root.$emit('bv::hide::tooltip')
      this.$store.dispatch('SetNode', node)
      const model = node.root.deflate()
      window.history.pushState({ treeData: model }, '')
    },
    startEditing (node) {
      this.$root.$emit('bv::hide::tooltip')
      node.startEditing()
      this.$nextTick(() => {
        const input = this.$refs[`input_${node.id}`]
        input.focus()
        input.setSelectionRange(0, input.value.length)
      })
    },
    stopEditing (node) {
      if (!this.timer) {
        node.stopEditing()
        this.treeUpdated(node)
        const self = this
        this.timer = setTimeout(() => {
          self.timer = null
        }, 100)
      }
    },
    getNodeLabel,
    initNodeData
  },
  data () {
    return {
      modal: {
        show: false,
        text: '',
        hash: '',
        title: ''
      },
      timer: null
    }
  },
  computed: {
    ...mapGetters(['treeData', 'loading'])
  }
}
</script>
<style lang="scss" scoped>
.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 25%;
}
.tree {
  .node-input {
    border: none;
    padding: 0;
    outline: none;
    background-color: rgba(0, 0, 0, 0.05);
    font-weight: bold;
    box-sizing: border-box;
  }
  .actions,
  .label,
  .sub-label {
    user-select: none;
  }
  .label {
    font-weight: bold;
  }
  .sub-label {
    font-size: smaller;
    font-weight: bold;
  }
  .proxdistalgoal-label {
    font-size: medium;
    font-weight: normal;
  }
}
</style>
